import { useEffect, useState } from 'react';
import { X, Cpu, MemoryStick, Users, RefreshCw, ExternalLink } from 'lucide-react';
import { api } from '../lib/api.js';

/** Antwort von GET /api/v1/stats/overview (öffentlich, read-only). */
interface Overview {
  at: number;
  cpu: { load1: number; cores: number; pct: number };
  mem: { total: number; used: number; free: number; pct: number };
  participants: number;
  activeAudio: number;
  uptimeSec: number;
  livekitOk: boolean;
}

/** Basis-URL des Admin-Backends aus der aktuellen Panel-URL ableiten
 *  (panel.intercom.livepuls.de → intercom.livepuls.de; Dev → :5173). */
function adminBaseUrl(): string {
  const override = (import.meta.env.VITE_ADMIN_BASE_URL as string | undefined)?.replace(/\/$/, '');
  if (override) return override;
  const { protocol, hostname, port } = window.location;
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
  if (isDev) return `${protocol}//${hostname}:5173`;
  const adminHost = hostname.startsWith('panel.') ? hostname.slice('panel.'.length) : hostname;
  const portPart = port && port !== '80' && port !== '443' ? `:${port}` : '';
  return `${protocol}//${adminHost}${portPart}`;
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/**
 * Admin-Overlay für Admin-Panels: unten eingeblendete Leiste mit Server-
 * auslastung (CPU/RAM/aktive Panels) + darunter das eingebettete Admin-Backend
 * zum Bearbeiten der Panels (iframe). Login/Bearbeiten passiert im iframe selbst.
 */
export function AdminBar({ onClose }: { onClose: () => void }): JSX.Element {
  const [stats, setStats] = useState<Overview | null>(null);
  const [err, setErr] = useState(false);
  const adminUrl = adminBaseUrl();

  useEffect(() => {
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        const s = await api.get<Overview>('/api/v1/stats/overview');
        if (alive) {
          setStats(s);
          setErr(false);
        }
      } catch {
        if (alive) setErr(true);
      }
    };
    void load();
    const t = window.setInterval(load, 3000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/60">
      <div className="flex h-[85vh] flex-col rounded-t-2xl border-t border-border bg-bg shadow-2xl">
        {/* Kopf: Titel + Schließen */}
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold text-text">Admin — Server &amp; Panels</h2>
          <div className="flex items-center gap-1">
            <a
              href={adminUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 rounded-full px-2 py-1 text-xxs uppercase tracking-wider text-text-dim hover:text-accent"
              title="Backend in neuem Tab öffnen (falls die Einbettung blockiert wird)"
            >
              <ExternalLink size={13} /> Neuer Tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 text-text-dim hover:text-text"
              aria-label="Schließen"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Serverauslastungs-Leiste */}
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-2.5">
          <Stat icon={<Cpu size={15} />} label="CPU" pct={stats?.cpu.pct} sub={stats ? `${stats.cpu.load1} / ${stats.cpu.cores}` : ''} />
          <Stat icon={<MemoryStick size={15} />} label="RAM" pct={stats?.mem.pct} sub={stats ? `${Math.round(stats.mem.used / 1e9 * 10) / 10}/${Math.round(stats.mem.total / 1e9)} GB` : ''} />
          <div className="flex items-center gap-1.5 text-text">
            <Users size={15} className="text-accent" />
            <span className="text-sm font-semibold">{stats?.participants ?? '–'}</span>
            <span className="text-xxs uppercase tracking-wider text-text-dim">aktive Panels</span>
          </div>
          {stats ? (
            <span className="text-xxs text-text-dim">Uptime {fmtUptime(stats.uptimeSec)}</span>
          ) : null}
          {err ? (
            <span className="flex items-center gap-1 text-xxs text-err">
              <RefreshCw size={12} /> Auslastung nicht erreichbar
            </span>
          ) : null}
        </div>

        {/* Eingebettetes Admin-Backend zum Bearbeiten der Panels */}
        <iframe
          title="Admin-Backend"
          src={adminUrl}
          className="min-h-0 flex-1 w-full border-0 bg-white"
        />
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  pct,
  sub,
}: {
  icon: JSX.Element;
  label: string;
  pct: number | undefined;
  sub: string;
}): JSX.Element {
  const p = pct ?? 0;
  const color = p >= 85 ? '#ef4444' : p >= 60 ? '#f59e0b' : '#22c55e';
  return (
    <div className="flex items-center gap-2">
      <span className="text-accent">{icon}</span>
      <div className="flex flex-col">
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-semibold text-text">{pct === undefined ? '–' : `${pct}%`}</span>
          <span className="text-xxs uppercase tracking-wider text-text-dim">{label}</span>
          {sub ? <span className="text-xxs text-text-dim">· {sub}</span> : null}
        </div>
        <div className="mt-0.5 h-1 w-24 overflow-hidden rounded-full bg-border">
          <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
}
